import { createContext, useContext, type ReactNode } from 'react'

/* ─── Root ─── */

const TileContext = createContext<{ flush: boolean }>({ flush: false })

interface TileProps {
  isLoading?: boolean
  flush?: boolean
  children: ReactNode
}

/**
 * Tile padding convention:
 *  - Default tiles use `p-4` from the `.card` utility (top + side + bottom).
 *  - `flush` tiles drop the root padding so their content (e.g. a divided
 *    `Tile.Stats` row) can hit the edges; `Tile.Header` then restores the
 *    `px-4 pt-4` spacing automatically via context, so callers should not
 *    hand-add `px-4 pt-4` to `Tile.Header`.
 */
function TileRoot({ isLoading, flush = false, children }: TileProps) {
  return (
    <TileContext.Provider value={{ flush }}>
      <div className={`card ${flush ? '!p-0 overflow-hidden' : ''}`}>
        {isLoading ? (
          <div className={`animate-pulse space-y-3 ${flush ? 'px-4 pt-4 pb-4' : ''}`}>
            <div className="h-6 bg-well rounded w-3/4" />
            <div className="h-4 bg-well rounded w-1/2" />
          </div>
        ) : (
          children
        )}
      </div>
    </TileContext.Provider>
  )
}

/* ─── Header ─── */

function Header({ children, className }: { children: ReactNode; className?: string }) {
  const { flush } = useContext(TileContext)
  const flushPadding = flush ? 'px-4 pt-4' : ''
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-2 border-b border-theme-border ${flushPadding} ${className || ''}`}
    >
      {children}
    </div>
  )
}

/* ─── Title ─── */

function Title({ children }: { children: ReactNode }) {
  return <h2 className="text-lg font-semibold text-primary">{children}</h2>
}

/* ─── Action ─── */

function Action({ children }: { children: ReactNode }) {
  return <>{children}</>
}

/* ─── Stats row ─── */

function Stats({ children }: { children: ReactNode }) {
  return <div className="flex items-center divide-x divide-theme-border">{children}</div>
}

/* ─── Single stat ─── */

function Stat({ value, label, color, wide }: { value: string; label: string; color?: string; wide?: boolean }) {
  return (
    <div className={`${wide ? 'flex-[1.5] sm:flex-1' : 'flex-1'} text-center pt-4 pb-4 min-w-0`}>
      <p className={`text-2xl font-bold ${color || 'text-primary'}`}>{value}</p>
      <p className="text-xs text-muted">{label}</p>
    </div>
  )
}

/* ─── Attach sub-components ─── */

export const Tile = Object.assign(TileRoot, {
  Header,
  Title,
  Action,
  Stats,
  Stat,
})
